import React, { useState } from "react"
import { GetApp } from "../../App"
import { classNames } from "../../Components"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { InfoDialog } from "../../Components/InfoDialog"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { IAccountPaymentSystem, IAccountSubscriptionStatus } from "../../Types"
import { AccountViewPlanBlockProps } from "./Params"

const daysUntil = (iso: string): number =>
  Math.floor((Date.parse(iso).valueOf() - Date.now()) / (24 * 60 * 60 * 1000));

export function AccountViewPlanBlock({ active, cancelCallback, canSubscribe, currency, plan, selectCallback }: AccountViewPlanBlockProps) {
  const app = GetApp()!;
  const t = app.getT("AccountView");
  // Apple (IAP) subscriptions can't be cancelled server-side — the Stripe cancel call
  // would silently no-op — so on the cloud/Stripe view an Apple sub gets an info dialog
  // pointing the user at the on-device Manage Subscriptions flow instead.
  const isAppleSubscription = app.account?.paymentSystem === IAccountPaymentSystem.apple;
  const isGiftSubscription = app.account?.paymentSystem === IAccountPaymentSystem.gift;
  const [showAppleCancelInfo, setShowAppleCancelInfo] = useState(false);
  return (
    <>
      <Block className={classNames(["AccountView-plan", `AccountView-plan-${plan.plan.name}`, active && "active"])}>
        <BlockRow>
          <h2 className="AccountView-plan-name">{t(`plan_${plan.plan.name}`)}</h2>
        </BlockRow>
        <BlockRow>
          <LocalizedMessage message={(m) => m.planDescriptions[plan.plan.name]} className="AccountView-plan-description" />
        </BlockRow>

        {plan.features.filter((f) => f.amount != 0).map((f) => {
          let feature = null;
          switch (f.feature) {
            case "active_ships":
            case "active_users":
            case "active_tenders":
              feature = <BlockRow className="AccountView-plan-info">{t(`feature_${f.feature}`, { amount: (f.amount ?? 0) < 0 ? t("feature_unlimited") : (f.amount ?? 0) })}</BlockRow>;
              break;
            case "support":
              feature = <BlockRow className="AccountView-plan-info">{t(`feature_support_${f.amount}`)}</BlockRow>;
              break;
            default:
              return null;
          }
          return (
            <React.Fragment key={f.feature}>
              {feature}
            </React.Fragment>
          );
        })}

        {plan.pricing.length == 0 ? (
          <BlockRow className="block-row-no-margin price-row">
            <BlockField className="AccountView-plan-price-info">
              <span className="price free-plan">{t("price_free")}</span>
            </BlockField>
          </BlockRow>
        ) : (
          <>
            {!active ? plan.pricing.sort((a, b) => b.duration - a.duration).map((p) => {
              if (p.currency != currency) {
                return;
              }
              const priceRowClass = plan.plan.name === "captain" ? "captain-price-row" :
                plan.plan.name === "master_captain" ? "master-captain-price-row" :
                  "block-row-no-margin";
              return (
                <>
                  <BlockRow className={priceRowClass}>
                    <BlockField className="AccountView-plan-price-info">

                      {p.duration > 30 ? (
                        <>
                          <span className="price block-row-actions">
                            {t(`price_${currency}`, { price: Math.floor(p.price / 12 / 100), duration: t(`price_duration_30`) })}
                          </span>
                        </>
                      ) : (
                        <span className="price block-row-actions">
                          {t(`price_${currency}`, { price: Math.floor(p.price / 100), duration: t(`price_duration_${p.duration}`) })}
                        </span>
                      )}

                    </BlockField>
                  </BlockRow>
                  <BlockRow className="AccountView-plan-price-info duration-info block-row-no-margin">
                    {t(`duration_${p.duration}`)}
                  </BlockRow>
                  {p.duration > 30 && (
                    <BlockRow className="block-row-top0">
                      <BlockField className="AccountView-plan-price-info">
                        <span className="note">
                          ({t(`price_${currency}`, { price: Math.floor(p.price / 100), duration: t(`price_duration_${p.duration}`) })})
                        </span>
                      </BlockField>
                    </BlockRow>
                  )}
                  <BlockRow>
                    <BlockField>
                      <button onClick={() => selectCallback(p)} className="button subscribe-button" disabled={!canSubscribe}>
                        {t("subscribe")}
                      </button>
                    </BlockField>
                  </BlockRow>
                </>
              );
            }) : (
              <>
                {app.account && app.account.subscriptionStatus == IAccountSubscriptionStatus.cancelled ? (
                  app.account.nextBillingAt && (
                    <BlockRow>
                      <BlockField>
                        <p><strong>{t("cancelled_ends", { days: daysUntil(app.account.nextBillingAt) })}</strong></p>
                      </BlockField>
                    </BlockRow>
                  )
                ) : (
                  <>
                    {app.account && app.account.nextBillingAt && (
                      <BlockRow>
                        <BlockField>
                          <p><strong>{t("renews_in", { days: daysUntil(app.account.nextBillingAt) })}</strong></p>
                        </BlockField>
                      </BlockRow>
                    )}
                    {!isGiftSubscription && (
                      <BlockRow actions={true} className="">
                        <BlockField>
                          {isAppleSubscription ? (
                            <button onClick={() => setShowAppleCancelInfo(true)} className="button subscribe-button">{t("cancel_plan")}</button>
                          ) : (
                            <button onClick={() => cancelCallback()} className="button subscribe-button" disabled={!app.account}>{t("cancel_plan")}</button>
                          )}
                        </BlockField>
                      </BlockRow>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </Block>
      {showAppleCancelInfo && (
        <InfoDialog
          title={t("apple_cancel_info_title")}
          message={t("apple_cancel_info_message")}
          onClose={() => setShowAppleCancelInfo(false)}
          style="info"
        />
      )}
    </>
  );
}
